/* eslint-disable react/no-deprecated */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@cargomatic/ui';

import _ from 'lodash';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';

import EquipmentActionCreators from 'actions/EquipmentActionCreators';
import EquipmentStore from '../../../stores/EquipmentStore';
import EquipmentRow from './EquipmentRow';
import ProTip from '../../ui/ProTip';
import { equipmentTypesMapping } from 'constants/Equipment';

const EQUIPMENT_ID_LENGTH_RANGE = [10, 11];

class BulkEditEquipmentModal extends Component {
  static propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func.isRequired,
    equipments: PropTypes.array,
  };

  static defaultProps = {
    show: false,
    equipments: [],
  };

  constructor(props) {
    super(props);
    this.state = {
      equipments: props.equipments.map((e) => ({
        ...e,
        isValid: true,
        error: {},
      })),
      status: '',
      saving: false,
      proTipVisible: true,
    };
  }

  componentDidMount() {
    EquipmentStore.addBulkUpdateChangeListener(
      this.handleBulkUpdateResultChange,
    );
  }

  componentWillReceiveProps(nextProps) {
    if (!_.isEqual(this.props.equipments, nextProps.equipments)) {
      const equipments = nextProps.equipments.map((e) => ({
        ...e,
        isValid: true,
        error: {},
      }));

      this.setState({ equipments });
    }
  }

  componentWillUnmount() {
    EquipmentStore.removeBulkUpdateChangeListener(
      this.handleBulkUpdateResultChange,
    );
  }

  handleBulkUpdateResultChange = () => {
    const { t } = this.props;
    const result = EquipmentStore.getBulkUpdateResult();

    if (result.successes.length > 0) {
      this.setState(
        {
          status: t('EQUIPMENTS_UPDATED_SUCCESSFULLY'),
        },
        () => {
          setTimeout(() => {
            this.setState({
              status: '',
            });
            this.handleHide();
          }, 4000);
        },
      );
    }
    this.setState({
      saving: false,
    });
  };

  handleClearAll = () => {
    const { equipments: currentEquipments } = this.state;

    const equipments = currentEquipments.map(resetEquipment);

    this.setState({
      equipments,
    });
  };

  handleSave = () => {
    this.setState(
      (currentState) => {
        return {
          equipments: this.validate(currentState.equipments),
        };
      },
      () => {
        if (this.state.equipments.every((e) => e.isValid)) {
          EquipmentActionCreators.bulkUpdateEquipments(
            this.transformRequest(this.state.equipments),
          );
        }
      },
    );
  };

  handleHide = () => this.props.onHide();

  handleFieldChange = (id, field, value) => {
    this.setState((currentState) => {
      const { equipments: currentEquipments } = currentState;
      const index = currentEquipments.findIndex((e) => e._id === id);

      const targetEquipment = _.cloneDeep(currentEquipments[index]);
      targetEquipment[field] = value;

      const isPrivateChassisAttributesResetRequired =
        targetEquipment?._equipmentType !== equipmentTypesMapping.CHASSIS ||
        !targetEquipment.is_private;

      if (isPrivateChassisAttributesResetRequired) {
        targetEquipment.is_private = null;
        targetEquipment.chassis_owner = null;
        targetEquipment.chassis_owner_type = null;
      }

      const isChassisOwnerUpdating =
        targetEquipment.chassis_owner_type &&
        targetEquipment.chassis_owner_type === 'cargomatic';

      if (isChassisOwnerUpdating) {
        targetEquipment.chassis_owner = null;
      }

      if (targetEquipment?._equipmentType !== equipmentTypesMapping.CONTAINER) {
        targetEquipment._equipmentSize = null;
        targetEquipment._type = '';
      }

      const equipments = [
        ...currentEquipments.slice(0, index),
        targetEquipment,
        ...currentEquipments.slice(index + 1),
      ];

      return {
        equipments,
      };
    });
  };

  handleDeselect = (id) => {
    this.setState((currentState) => {
      const { equipments: currentEquipments } = currentState;

      const equipments = currentEquipments.filter((e) => e._id !== id);

      return {
        equipments,
      };
    });
  };

  handleApplyToAll = (id) => {
    this.setState((currentState) => {
      const { equipments: currentEquipments } = currentState;

      const index = currentEquipments.findIndex((eq) => eq._id === id);

      const targetEquipment = currentEquipments[index];
      const {
        status,
        _type,
        _equipmentType,
        _equipmentSize,
        is_private,
        chassis_owner,
        chassis_owner_type,
      } = targetEquipment;

      const equipments = currentEquipments.map((eq, i) => {
        let mapped = {
          ...eq,
        };

        if (index === i) {
          return mapped;
        }

        /**
         * Apply all will work only on same assets type
         */
        if (mapped?._equipmentType === targetEquipment?._equipmentType) {
          mapped = {
            ...mapped,
            status,
            _type,
            _equipmentType,
            _equipmentSize,
            is_private,
            chassis_owner,
            chassis_owner_type,
          };
        }

        return mapped;
      });

      return {
        equipments,
      };
    });
  };
  handleCloseTip = () => this.setState({ proTipVisible: false });

  transformRequest(equipments) {
    return equipments.map((eq) => {
      const {
        _type,
        _equipmentSize,
        _equipmentType,
        equipment_id,
        status,
        is_private,
        chassis_owner,
        chassis_owner_type,
      } = eq;

      const chassis_owner_id =
        chassis_owner?._id && chassis_owner_type === 'customer'
          ? chassis_owner._id
          : null;

      return {
        _id: eq._id,
        updates: {
          _type,
          _equipmentSize,
          _equipmentType,
          equipment_id,
          status,
          is_private,
          chassis_owner_type,
          chassis_owner: chassis_owner_id,
        },
      };
    });
  }

  validate(equipments) {
    return equipments.map(this.validateEquipment);
  }

  validateEquipment = (equipment, i) => {
    const error = {};
    const { t } = this.props;

    const {
      equipment_id,
      _equipmentType,
      _equipmentSize,
      _type,
      status,
      is_private,
      chassis_owner,
      chassis_owner_type,
    } = equipment;

    const rowNumber = i + 1;

    if (!equipment_id) {
      error.equipment_id = `${t('EQUIPMENT')} ${rowNumber} - ${t(
        'ENTER_EQUIPMENT_NUMBER',
      )}`;
    } else if (
      _equipmentType !== 'trailer' &&
      !EQUIPMENT_ID_LENGTH_RANGE.includes(equipment_id.length)
    ) {
      error.equipment_id = `${t('EQUIPMENT')} ${rowNumber} - ${t(
        'EQUIPMENT_NUMBER_EXPECTED_LENGTH',
        {
          expectedLengths: EQUIPMENT_ID_LENGTH_RANGE.join(' or '),
        },
      )}`;
    }

    if (!_equipmentType) {
      error._equipmentType = `${t('EQUIPMENT')} ${rowNumber} - ${t(
        'PLEASE_SELECT_ASSET_TYPE',
      )}`;
    } else if (_equipmentType === 'container') {
      if (!_type) {
        error._type = `${t('EQUIPMENT')} ${rowNumber} - ${t(
          'PLEASE_SELECT_EQUIPMENT_TYPE',
        )}`;
      }
      if (!_equipmentSize) {
        error._equipmentSize = `${t('EQUIPMENT')} ${rowNumber} - ${t(
          'SELECT_EQUIPMENT_SIZE',
        )}`;
      }
    }

    if (!status) {
      error.status = `${t('EQUIPMENT')} ${rowNumber} - ${t(
        'PLEASE_SELECT_EQUIPMENT_STATUS',
      )} `;
    }

    const isChassisPrivate =
      _equipmentType === equipmentTypesMapping.CHASSIS && is_private;
    const requiredChassisOwnerType = isChassisPrivate && !chassis_owner_type;
    const requiredChassisOwner =
      chassis_owner_type && chassis_owner_type == 'customer' && !chassis_owner;

    if (requiredChassisOwnerType) {
      error.chassis_owner_type = `${t('EQUIPMENT')} ${rowNumber} - ${t(
        'PLEASE_SELECT_CHASSIS_OWNER',
      )} `;
    }

    if (requiredChassisOwner) {
      error.chassis_owner = `${t('EQUIPMENT')} ${rowNumber} - ${t(
        'PLEASE_SELECT_CUSTOMER',
      )} `;
    }

    return {
      ...equipment,
      error,
      isValid: Object.keys(error).length === 0,
    };
  };

  validateOrderTypes = () => {
    const { equipments } = this.props;

    const orderTypes = _.groupBy(equipments, (e) => {
      if (e.order) {
        const {
          order: { order_type },
        } = e;

        return order_type;
      }
      return null;
    });

    return Object.keys(orderTypes).length === 1;
  };

  renderEquipmentForms = () => {
    const { t } = this.props;
    const { equipments } = this.state;

    if (equipments.length === 0) {
      return <p>Please select the equipments you would like to update</p>;
    }

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[150px]">{t('ID')}</TableHead>
            <TableHead className="min-w-[150px]">{t('ASSET_TYPE')}</TableHead>
            <TableHead className="min-w-[160px]">{t('TYPE')}</TableHead>
            <TableHead className="min-w-[150px]">
              {t('SIZE', { count: 2 })}
            </TableHead>
            <TableHead className="min-w-[90px]">{t('PRIVATE')}</TableHead>
            <TableHead className="min-w-[170px]">
              {t('CHASSIS_OWNERSHIP')}
            </TableHead>
            <TableHead className="min-w-[240px]">{t('CUSTOMER')}</TableHead>
            <TableHead className="min-w-[160px]">{t('STATUS')}</TableHead>
            <TableHead className="min-w-[110px]">{t('APPLY_TO_ALL')}</TableHead>
            <TableHead>&nbsp;</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {this.state.equipments.map((equipment) => (
            <EquipmentRow
              key={equipment._id}
              equipment={equipment}
              onApplyToAll={this.handleApplyToAll}
              onFieldChange={this.handleFieldChange}
              onDeselect={this.handleDeselect}
            />
          ))}
        </TableBody>
      </Table>
    );
  };
  renderProTip() {
    const { t } = this.props;
    if (!this.state.proTipVisible) {
      return null;
    }

    return (
      <ProTip
        message={t('BULKEDIT_PRO_TIP')}
        className={{ 'mb-4': true }}
        onClose={this.handleCloseTip}
      />
    );
  }
  renderStatus() {
    if (!this.state.status) {
      return null;
    }
    return (
      <Alert className="border-green text-green-dark">
        <AlertTitle>{this.state.status}</AlertTitle>
      </Alert>
    );
  }
  renderValidationErrors() {
    const { t } = this.props;
    let validationHeader = t('CORRECT_ISSUES');
    if (this.state.equipments.every((e) => e.isValid)) {
      return null;
    }
    const flattenedErrors = this.state.equipments.reduce((errorList, eq) => {
      const errors = Object.values(eq.error);
      return errorList.concat(errors);
    }, []);
    return (
      <Alert variant="destructive">
        <AlertTitle>{validationHeader}</AlertTitle>
        <AlertDescription>
          <ul>
            {flattenedErrors.map((error, i) => (
              <li key={i}>{error}</li>
            ))}
          </ul>
        </AlertDescription>
      </Alert>
    );
  }
  render() {
    const { t } = this.props;
    return (
      <Dialog
        open={this.props.show}
        onOpenChange={(open) => {
          if (!open) {
            this.handleHide();
          }
        }}
      >
        <DialogContent
          className="max-w-6xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{titleize(t('BULK_EDIT_EQUIPMENT'))}</DialogTitle>
          </DialogHeader>
          {this.state.proTipVisible && this.renderProTip()}
          {this.renderEquipmentForms()}
          <DialogFooter>
            <div className="flex flex-wrap items-start justify-end">
              <div className="mr-4">
                {this.renderStatus()}
                {this.renderValidationErrors()}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={this.state.saving}
                  onClick={this.handleClearAll}
                >
                  {titleize(t('CLEAR_ALL'))}
                </Button>
                <Button onClick={this.handleSave} disabled={this.state.saving}>
                  {this.state.saving && (
                    <div className="flex items-center">
                      <i className="fa fa-spin fa-circle-o-notch mr-1" />
                      <span>{titleize(t('SAVING'))}...</span>
                    </div>
                  )}
                  {!this.state.saving && <span>{titleize(t('SAVE'))}</span>}
                </Button>
                <Button
                  variant="outline"
                  disabled={this.state.saving}
                  onClick={this.handleHide}
                >
                  {titleize(t('CLOSE'))}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
}

function resetEquipment(equipment) {
  return {
    ...equipment,
    _type: '',
    status: null,
    _equipmentType: '',
    _equipmentSize: null,
    is_private: false,
    chassis_owner_type: null,
    chassis_owner: null,
  };
}

export default withTranslation()(BulkEditEquipmentModal);
